import React, { useEffect, useState } from 'react';
import { useFeedbackForm } from './useFeedbackForm';
import CandidateInfoTab from './CandidateInfoTab';
import PositivesTab from './PositivesTab';
import ImprovementsTab from './ImprovementsTab';
import FinalTab from './FinalTab';
import { FeedbackFormProps } from './schema';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const FeedbackForm = (props: FeedbackFormProps) => {
  const { 
    form, 
    currentTab, 
    isSubmitting, 
    isReadOnly,
    requestData,
    contextLabels,
    handleTabChange, 
    goToNextTab, 
    goToPreviousTab, 
    onSubmit 
  } = useFeedbackForm(props);

  const [projects, setProjects] = useState<{id: string; name: string; category: string}[]>([]);
  const [selectedProject, setSelectedProject] = useState<string | null>(
    requestData.projectId || null
  );
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ 
    name: '', 
    description: '', 
    category: requestData.contextType || 'recrutement' 
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      const fetchProjects = async () => {
        try {
          const { data, error } = await supabase
            .from('projects')
            .select('id, name, category')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (error) throw error;
          if (data) setProjects(data);
        } catch (error) {
          console.error('Erreur lors du chargement des projets:', error);
        }
      };
      
      fetchProjects();
    }
  }, [user]);

  const handleCreateProject = async () => {
    if (!user || !newProject.name.trim()) return;
    
    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          name: newProject.name,
          description: newProject.description,
          category: newProject.category,
          user_id: user.id
        })
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setProjects([...projects, data[0]]);
        setSelectedProject(data[0].id);
        form.setValue('projectId', data[0].id);
      }
      
      setCreateProjectDialogOpen(false);
      setNewProject({ name: '', description: '', category: requestData.contextType || 'recrutement' });
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    form.setValue('projectId', value);
  };

  const ProjectSelector = () => (
    <div className="space-y-2 mt-4">
      <Label>Associer à un projet (optionnel)</Label>
      <div className="flex gap-2">
        <Select value={selectedProject || 'none'} onValueChange={handleProjectChange}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Sélectionner un projet ou aucun" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucun projet</SelectItem>
            {projects.map(project => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button 
          type="button" 
          variant="outline" 
          className="flex gap-1 items-center"
          onClick={() => setCreateProjectDialogOpen(true)}
        >
          <Plus className="h-4 w-4" /> Nouveau
        </Button>
      </div>
    </div>
  );

  return (
    <div className="container max-w-3xl mx-auto">
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">
            {requestData.title || (props.isRecruiter ? "Créer un feedback pour un candidat" : "Créer un feedback")}
          </CardTitle>
          <CardDescription>
            {props.isRecruiter 
              ? "Rédigez un feedback détaillé et constructif pour un candidat"
              : "Rédigez un feedback détaillé et constructif"}
            {requestData.recruiterName && ` pour ${requestData.recruiterName}`}
            {requestData.recruiterCompany && ` de ${requestData.recruiterCompany}`}
          </CardDescription>
        </CardHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Tabs value={currentTab} onValueChange={handleTabChange}>
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="details">Informations</TabsTrigger>
                <TabsTrigger value="positives">Points forts</TabsTrigger>
                <TabsTrigger value="improvements">Axes d'amélioration</TabsTrigger>
                <TabsTrigger value="final">Finalisation</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details">
                <CardContent className="space-y-4 pt-4">
                  <CandidateInfoTab form={form} isReadOnly={isReadOnly} contextLabels={contextLabels} />
                  <ProjectSelector />
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button type="button" onClick={goToNextTab}>Suivant</Button>
                </CardFooter>
              </TabsContent>
              
              <TabsContent value="positives">
                <CardContent className="space-y-4 pt-4">
                  <PositivesTab form={form} />
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button type="button" variant="outline" onClick={goToPreviousTab}>Précédent</Button>
                  <Button type="button" onClick={goToNextTab}>Suivant</Button>
                </CardFooter>
              </TabsContent>
              
              <TabsContent value="improvements">
                <CardContent className="space-y-4 pt-4">
                  <ImprovementsTab form={form} />
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button type="button" variant="outline" onClick={goToPreviousTab}>Précédent</Button>
                  <Button type="button" onClick={goToNextTab}>Suivant</Button>
                </CardFooter>
              </TabsContent>
              
              <TabsContent value="final">
                <CardContent className="space-y-4 pt-4">
                  <FinalTab form={form} />
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button type="button" variant="outline" onClick={goToPreviousTab}>Précédent</Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer le feedback'}
                  </Button>
                </CardFooter>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </Card>

      <Dialog open={createProjectDialogOpen} onOpenChange={setCreateProjectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un nouveau projet</DialogTitle>
            <DialogDescription>
              Organisez vos feedbacks et demandes en projets
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Nom du projet</Label>
              <Input 
                id="project-name" 
                value={newProject.name}
                onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                placeholder="Ex: Recrutement Développeurs Frontend"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea 
                id="project-description"
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                placeholder="Description du projet (optionnelle)"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-category">Catégorie</Label>
              <Select
                value={newProject.category}
                onValueChange={(value) => setNewProject({...newProject, category: value})}
              >
                <SelectTrigger id="project-category">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recrutement">Recrutement</SelectItem>
                  <SelectItem value="candidature">Candidature</SelectItem>
                  <SelectItem value="mission">Mission</SelectItem>
                  <SelectItem value="managérial">Managérial</SelectItem>
                  <SelectItem value="collaboration">Collaboration</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateProjectDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateProject}>Créer le projet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackForm;
